import {BitmapText, Container, Graphics, IBitmapTextStyle, Rectangle} from "pixi.js";
import {inches, lineBlueHeavy} from "../../const";
import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";

// length of each arm of the 45° tick mark drawn at the dimension line's ends.
const DIM_TICK = 1 * inches;

// padding on each side of the text within the break in the dimension line.
const DIM_TEXT_PAD = inches;

// gap between the dimension line and the near edge of the label when the span is
// too short to break the line for the text. The label is lifted clear of the line
// (above a horizontal line, left of a vertical one) instead of rendering on it.
const DIM_TEXT_OFFSET = 0.5 * inches;

const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 40,
};

export type LinearDimensionDraw = {
    axis: "horizontal" | "vertical";

    // start/end coordinate along the axis, in the container's local space (the
    // horizontal axis runs in X, the vertical axis in Y).
    from: number;
    to: number;

    // the fixed cross-axis coordinate: the line's Y for a horizontal dimension,
    // its X for a vertical one.
    cross: number;

    // the value shown on the label. May differ from any single backing FrmVar
    // (e.g. a threshold clearance that sums bottomGap + under/overcut), which is
    // why edits go through onSet rather than a FrmVar handed in here.
    value: number;

    // renders in the user color when true (the backing var is user-modified).
    modified: boolean;

    // override the along-axis position of the number label (its X for a
    // horizontal dimension, its Y for a vertical one). Defaults to the span
    // midpoint. Use to move a cramped label clear of a short dimension — e.g. off
    // the latch edge. When set, the line is drawn unbroken (the label sits beside
    // the line rather than in a gap).
    overrideNumberPlacement?: number;

    // commit an edited value / clear the user override. Both should re-run the
    // owning layout so the new value is read back and everything redraws.
    onSet: (value: number) => void;
    onClear: () => void;
};

// a single engineering-style linear dimension between two arbitrary points along
// one axis: 45° ticks at each end and the line broken in the middle for a
// clickable value. Tapping the label opens the app's dimension editor wired to
// onSet/onClear. Unlike FrameBlockInnerDimension (which always spans a target's
// whole width/height from its origin), this dimensions an arbitrary sub-span —
// e.g. the gap between a pair's leaves, or the clearance under a door leaf.
export class LinearDimension {
    private container: Container;
    private app: DrawFrmApp;

    // dimension lines live in their own Graphics so they don't fight the leaf's
    // fill/stroke; the label is a sibling so taps hit the text, not the lines.
    private lines: Graphics;
    private label: BitmapText | null = null;

    // the latest value / edit callbacks, refreshed every draw() so the label's
    // tap handler always edits against the current geometry.
    private value = 0;
    private modified = false;
    private onSet: (value: number) => void = () => {};
    private onClear: () => void = () => {};

    // where the line lies while shown (see lineAt).
    private line: {axis: "horizontal" | "vertical"; cross: number} | null = null;

    constructor(container: Container, app: DrawFrmApp) {
        this.container = container;
        this.app = app;
        this.lines = new Graphics();
        container.addChild(this.lines);
    }

    draw(props: LinearDimensionDraw) {
        this.value = props.value;
        this.modified = props.modified;
        this.onSet = props.onSet;
        this.onClear = props.onClear;
        this.line = {axis: props.axis, cross: props.cross};

        const g = this.lines;
        g.clear();

        const style = this.app.dimensionStyle;
        const color = props.modified ? style.userColor : style.defaultLineColor;

        const label = this.ensureLabel();
        label.visible = true;
        label.text = this.app.formatDimension(props.value);
        label.tint = color;

        const lo = Math.min(props.from, props.to);
        const hi = Math.max(props.from, props.to);
        const mid = (lo + hi) / 2;
        const span = hi - lo;

        // where the number sits along the axis, and whether we break the line for
        // it. An override moves the label off the line, so the line stays whole.
        const along = props.overrideNumberPlacement ?? mid;
        const broken = props.overrideNumberPlacement === undefined;

        this.app.setDimensionLineStyle(g, color);

        if (props.axis === "horizontal") {
            const y = props.cross;
            label.rotation = 0;
            // hit-test the text's bounding box so taps in the whitespace register.
            label.hitArea = new Rectangle(-label.width * 0.5, -label.height * 0.5, label.width, label.height);

            drawTick(g, lo, y);
            drawTick(g, hi, y);

            const half = label.width * 0.5 + DIM_TEXT_PAD;
            if (broken && span > half * 2) {
                // line broken in the middle to make room for the text, on the line
                label.position.set(along, y);
                g.moveTo(lo, y); g.lineTo(mid - half, y);
                g.moveTo(mid + half, y); g.lineTo(hi, y);
            } else {
                // too short to break (or the label was placed off the line via an
                // override): draw the line whole. Without an override, lift the
                // label above the line so it doesn't render on top of it.
                const lift = broken ? label.height * 0.5 + DIM_TEXT_OFFSET : 0;
                label.position.set(along, y - lift);
                g.moveTo(lo, y); g.lineTo(hi, y);
            }
        } else {
            const x = props.cross;
            label.rotation = -Math.PI / 2; // reads up the line
            label.hitArea = new Rectangle(-label.width * 0.5, -label.height * 0.5, label.width, label.height);

            drawTick(g, x, lo);
            drawTick(g, x, hi);

            const half = label.width * 0.5 + DIM_TEXT_PAD; // text is rotated, so width runs along Y
            if (broken && span > half * 2) {
                label.position.set(x, along);
                g.moveTo(x, lo); g.lineTo(x, mid - half);
                g.moveTo(x, mid + half); g.lineTo(x, hi);
            } else {
                // too short to break: keep the line whole and lift the label to the
                // left of it (the text reads up, so its height runs along X).
                const lift = broken ? label.height * 0.5 + DIM_TEXT_OFFSET : 0;
                label.position.set(x - lift, along);
                g.moveTo(x, lo); g.lineTo(x, hi);
            }
        }
    }

    // hide the whole dimension (lines + label) without tearing it down; used when
    // the "show all dimensions" view toggle is off or the opening isn't a pair.
    hide() {
        this.lines.clear();
        if (this.label) this.label.visible = false;
        this.line = null;
    }

    // the axis and cross position of the line while it is shown, else null.
    lineAt(): {axis: "horizontal" | "vertical"; cross: number} | null {
        return this.line;
    }

    // open the value editor anchored on the number, as if the user tapped it.
    // Public so an owner can offer another way in (a stick opens it on
    // double-click). No-op while the number isn't on screen: the callbacks and
    // the anchor position both come from the last draw().
    openEditor() {
        if (this.app.readOnly) return;
        const t = this.label;
        if (!t || t.destroyed || !t.visible) return;

        this.app.showReact({
            render: (opts) => this.app.renderDimension({
                value: this.value,
                locked: this.modified,
                onLockChange: () => {
                    this.onClear();
                    opts.remove();
                },
                onDone: (value: number) => {
                    opts.remove();
                    this.onSet(value);
                },
                onCancel: () => {
                    opts.remove();
                },
            }),
            anchor: () => t.getGlobalPosition(),
        });
    }

    private ensureLabel(): BitmapText {
        if (this.label) return this.label;

        const t = new BitmapText("", TEXT_STYLE);
        t.anchor.set(0.5, 0.5);
        t.tint = lineBlueHeavy;

        t.eventMode = "static";
        t.cursor = "pointer";
        t.on("pointertap", (e) => {
            e.stopPropagation();
            this.openEditor();
        });

        this.container.addChild(t);
        this.label = t;
        return t;
    }

    destroy() {
        // a parent may have already freed these as part of its own pixi tree
        // destroy (children:true) — guard so we don't double-destroy (a second
        // BitmapText.destroy reads its nulled texture cache and throws).
        if (this.label && !this.label.destroyed) this.label.destroy();
        this.label = null;
        if (!this.lines.destroyed) this.lines.destroy();
    }
}

// draws a "/" slash centered on (x, y) at 45°, used as the tick at each end of a
// dimension line.
function drawTick(g: Graphics, x: number, y: number) {
    g.moveTo(x - DIM_TICK, y + DIM_TICK);
    g.lineTo(x + DIM_TICK, y - DIM_TICK);
}
